import { alpha, SxProps, Theme } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';

const toolbarItem =
  ':is(.rdw-inline-wrapper, .rdw-list-wrapper, .rdw-text-align-wrapper, .rdw-link-wrapper, .rdw-history-wrapper)';

export const wysiwygControlSx = ({
  toolbarDisabled,
  contentDisabled,
}: {
  toolbarDisabled?: boolean;
  contentDisabled?: boolean;
}): SxProps<Theme> => ({
  '& .rdw-editor-wrapper': {
    padding: '6px',
    borderRadius: borderRadius('s'),
    boxShadow: t => `0 0 0 1px ${alpha(t.palette.text.primary, 0.23)}`,
    '&:hover': {
      boxShadow: t => `0 0 0 1px ${alpha(t.palette.text.primary, 1)}`,
    },
    '&:focus-within': {
      boxShadow: t => `0 0 0 2px ${t.palette.primary.main}`,
    },
  },
  '& .rdw-editor-toolbar': {
    position: 'relative',
    borderRadius: '10px',
    display: 'flex',
    width: 'fit-content',
    justifyContent: 'flex-start',
    background: t => t.palette.other.filledInputBackground,
    flexWrap: 'wrap',
    fontSize: '14px',
    userSelect: 'none',
    ...(toolbarDisabled && {
      pointerEvents: 'none',
      filter: 'opacity(0.5)',
    }),
  },
  '& .rdw-option-wrapper': {
    cursor: 'pointer',
    padding: '10px 6px',
    '&:hover': {
      background: t => t.palette.action.hover,
      transition: 'background 200ms ease-in-out',
    },
    '& img': {
      width: '18px',
      height: '18px',
      filter: t => (t.palette.mode === 'light' ? 'invert(1)' : 'none'),
    },
  },
  [`& ${toolbarItem}`]: {
    display: 'flex',
    flexDirection: 'row',
    height: 'fit-content',
    alignItems: 'center',
    justifyContent: 'center',
  },
  [`& ${toolbarItem}:first-child .rdw-option-wrapper:first-child`]: {
    borderTopLeftRadius: '10px',
    borderBottomLeftRadius: '10px',
    paddingLeft: '10px',
  },
  [`& ${toolbarItem}:last-child .rdw-option-wrapper:last-child`]: {
    borderTopRightRadius: '10px',
    borderBottomRightRadius: '10px',
    paddingRight: '10px',
  },
  '& .rdw-link-modal': {
    position: 'absolute',
    fontSize: '12px',
    top: '40px',
    left: '208px',
    display: 'flex',
    flexDirection: 'column',
    width: '235px',
    border: t => `1px solid ${t.palette.other.outlinedBorder}`,
    padding: '15px',
    borderRadius: '8px',
    color: 'text.primary',
    zIndex: 100,
    background: t => t.palette.background.paper,
  },
  '& .rdw-editor-main': {
    maxHeight: '400px',
    overflow: 'auto',
    color: 'text.primary',
    ...(contentDisabled && {
      color: 'text.secondary',
      cursor: 'not-allowed',
    }),
  },
  // In case of long paragraphs, the editor keeps scrolling to bottom when pressing ENTER key,
  // https://github.com/facebook/draft-js/issues/304#issuecomment-476951825
  '& .DraftEditor-root': {
    overflow: 'auto',
  },
  '& .rdw-option-active': {
    background: t => t.palette.action.selected,
    transition: 'background 200ms ease-in-out',
    '&:hover': {
      background: t => t.palette.action.hover,
      transition: 'background 200ms ease-in-out',
    },
  },
  '& .rdw-link-modal-input': {
    padding: '8px 25px',
    border: t => `1px solid ${t.palette.other.outlinedBorder}`,
    borderRadius: '5px',
    fontSize: '12px',
    transition:
      'background-color 5000s ease-in-out 0s, border-color .2s ease-in',
    background: 'transparent !important',
    width: '100%',
    color: 'text.primary',
    fontFamily: 'inherit',
    '&:focus-visible': {
      outline: 'none',
      border: t => `0.5px solid ${t.palette.primary.main}`,
      borderRadius: '5px',
    },
  },
  '& .rdw-link-modal-target-option#openLinkInNewWindow': {
    margin: '10px 10px 0px 0px',
  },
  '& .rdw-link-modal-label': {
    marginTop: '4px',
  },
  '& .rdw-link-modal-btn': {
    borderRadius: '5px',
    fontSize: '11px',
    border: 'none',
    padding: '3px 10px',
    backgroundColor: 'text.secondary',
    '&:disabled': {
      borderRadius: '5px',
      backgroundColor: t => t.palette.action.disabledBackground,
      cursor: 'not-allowed',
    },
  },
  '& .rdw-link-modal-buttonsection': {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row-reverse',
    margin: '12px 0px 0px 0px',
  },
  '& .public-DraftStyleDefault-ltr': {
    textAlign: 'inherit !important',
  },
  '& .rdw-center-aligned-block *': {
    textAlign: 'center !important',
  },
  '& .rdw-right-aligned-block *': {
    textAlign: 'right !important',
  },
  '& .public-DraftEditor-content': {
    minHeight: '250px',
    padding: '15px 5px',
  },
});
